import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { account } from '../lib/appwrite';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';

const ResetPassword = () => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Get userId and secret from URL params (sent by Appwrite recovery email)
  const userId = searchParams.get('userId');
  const secret = searchParams.get('secret');

  useEffect(() => {
    // Check if we have the required params from the recovery email
    if (!userId || !secret) {
      setError('Invalid or expired password reset link. Please request a new one.');
    }
  }, [userId, secret]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');

    // Validation
    if (newPassword?.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!userId || !secret) {
      setError('Invalid reset link. Please request a new password reset.');
      return;
    }

    setLoading(true);

    try {
      // Use Appwrite's updateRecovery with the userId and secret from the email link
      await account.updateRecovery(userId, secret, newPassword);
      setSuccess(true);
    } catch (error) {
      console.error('Password update error:', error);
      if (error.code === 401) {
        setError('This password reset link has expired. Please request a new one.');
      } else {
        setError(error?.message || 'Failed to update password. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 px-4">
      <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-2xl shadow-xl">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-gray-900">Set New Password</h2>
          <p className="mt-2 text-sm text-gray-600">
            Enter your new password below
          </p>
        </div>

        {success ? (
          <div className="mt-8 space-y-6">
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
              <p className="font-medium">Password updated successfully!</p>
              <p className="text-sm mt-1">You can now login with your new password.</p>
            </div>
            <Button
              onClick={() => navigate('/login')}
              className="w-full !text-white !font-bold"
            >
              Go to Login
            </Button>
          </div>
        ) : (
          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                {error}
              </div>
            )}

            <div className="space-y-4">
              <Input
                label="New Password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e?.target?.value)}
                required
                placeholder="Enter new password"
                minLength={8}
                disabled={!userId || !secret}
              />

              <Input
                label="Confirm New Password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e?.target?.value)}
                required
                placeholder="Confirm new password"
                minLength={8}
                disabled={!userId || !secret}
              />
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                <strong>Password Requirements:</strong>
              </p>
              <ul className="text-sm text-blue-700 mt-2 ml-4 list-disc space-y-1">
                <li>At least 8 characters long</li>
                <li>Passwords must match</li>
              </ul>
            </div>

            <Button
              type="submit"
              className="w-full !text-white !font-bold"
              disabled={loading || !userId || !secret}
            >
              {loading ? 'Updating...' : 'Update Password'}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;